import axios from 'axios'
import config from '@/config.js'
import { getStatic } from '@/services/data-source.js'
import {
  normalizeBiblabels,
  normalizeBiblabelSelection,
} from '@/services/biblabels.js'

const API_URL = config.apiUrl
const API_URL_CLIENTSIDE = config.apiUrlClientSide

const state = {
  languages: [],
  genres: [],
  heads: [],
  genresAll: [],
  scriptas: [],
  localizations: [],
  metatexts: [],
  ontologies: [],
  loadingStatus: false,
  tokentypes: [],
  dictionaries: [],
  etymologySkeletons: {},
  biblabels: [],
  articleauthors: [],
  appSettings: {}
}

const mutations = {
  setGenres(state, genres){
    state.genres = genres
  },
  setLocalizations(state, localizations){
    state.localizations = localizations
  },
  setMetatexts(state, metatexts) {
    state.metatexts = metatexts
  },
  setAllGenres(state, genres){
    state.genresAll = genres
  },
  setHeads(state, heads){
    state.heads = heads
  },
  setAnnotators(state, annotators){
    state.annotators = annotators
  },
  setBiblabels(state, biblabels){
    state.biblabels = biblabels
  },
  setEtymologySkeletons(state, etymologySkeletons){
    state.etymologySkeletons = {}
    for (let etymologySkeleton of etymologySkeletons){
      state.etymologySkeletons[etymologySkeleton.acronym] = etymologySkeleton
    }
  },
  setArticleauthors(state, articleauthors){
    state.articleauthors = articleauthors
  },
  setScriptas(state, scriptas){
    state.scriptas = scriptas
  },
  setLanguages(state, languages){
    state.languages = languages
  },
  setDictionaries(state, dictionaries){
    state.dictionaries = dictionaries
  },
  setOntologies(state, ontologies){
    state.ontologies = ontologies
  },
  loadingStatus(state, status){
    state.loadingStatus = status
  },
  setTokentype(state, tokentypes){
    state.tokentypes = tokentypes
  },
  setAppSettings(state, settings){
    state.appSettings = settings
  }
}

const getters = {
  appSettings: function(state){
    return state.appSettings
  },
  getBibLabelsByIds: (state) => (ids) => {
    const selectedIds = new Set((ids || []).map(String))
    return state.biblabels.filter(
      biblabel => selectedIds.has(String(biblabel.id))
    )
  },
  getDictionariesByIds: (state) => (ids) => {
    return state.dictionaries.filter(dictionary => ids.includes(dictionary.id))
  },
  getUUID: () => () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    })
  }
}

const actions = {
  loadingStatus({ commit }, status){
    commit('loadingStatus', status)
  },
  searchGenres({ commit }, options){
    let query = options.query
    let exclude = (options.exclude || []).join(',')
    axios
      .get(`${API_URL}/genres/autocomplete?query=${query}&exclude=${exclude}`)
      .then(r => r.data)
      .then(genres => {
        commit('setGenres', genres)
      })
  },
  loadGenres({ commit }){
    let url = `${API_URL}/genres/all`
    if (!config.editable) {
      url = `${API_URL_CLIENTSIDE}/genres.json`
    }
    axios
      .get(url)
      .then(r => r.data)
      .then(genres => {
        commit('setAllGenres', genres)
      })
  },
  loadHeads({ commit }){
    axios
      .get(`${API_URL}/heads/all`)
      .then(r => r.data)
      .then(heads => {
        commit('setHeads', heads)
      })
  },
  loadAppSettings({ commit }){
    axios
      .get(`${API_URL}/settings`)
      .then(r => r.data)
      .then(settings => {
        commit('setAppSettings', settings)
      })
  },
  loadEtymologySkeletons({ commit }){
    axios
      .get(`${API_URL}/etymologyskeleton/all`)
      .then(r => r.data)
      .then(etymologySkeletons => {
        commit('setEtymologySkeletons', etymologySkeletons)
      })
  },
  loadAnnotators({ commit }){
    axios
      .get(`${API_URL}/annotators/all`)
      .then(r => r.data)
      .then(annotators => {
        commit('setAnnotators', annotators)
      })
  },
  loadBiblabels({ commit, dispatch, rootState }){
    const request = config.staticData
      ? getStatic('biblabels.json')
      : axios.get(`${API_URL}/biblabels/all`)
    return request
      .then(r => r.data)
      .then(data => {
        const biblabels = normalizeBiblabels(data)
        commit('setBiblabels', biblabels)
        const selection = normalizeBiblabelSelection(
          rootState.user.settingsPI.biblabels,
          biblabels
        )
        commit('cUpdateUserSettingsPI', {
          property: 'biblabels',
          value: selection,
        })
        return biblabels
      })
      .catch(error => {
        commit('setBiblabels', [])
        dispatch('loadingStatus', false)
        dispatch('addNotification', {
          type: 'danger',
          text: error.message || 'Bibliography labels could not be loaded',
        })
        return []
      })
  },
  loadArticleauthors({ commit }){
    axios
      .get(`${API_URL}/articleauthors/all`)
      .then(r => r.data)
      .then(articleauthors => {
        commit('setArticleauthors', articleauthors)
      })
  },
  loadDictionaries({ commit, dispatch }){
    const request = config.staticData
      ? getStatic('dictionaries.json')
      : axios.get(`${API_URL}/dictionaries/all`)
    return request
      .then(r => r.data)
      .then(dictionaries => {
        commit('setDictionaries', dictionaries)
        return dictionaries
      })
      .catch(error => {
        commit('setDictionaries', [])
        dispatch('addNotification', {
          type: 'danger',
          text: error.message || 'Dictionaries could not be loaded',
        })
        return []
      })
  },
  loadLocalizations({ commit }){
    let url = `${API_URL}/localizations/all`
    if (!config.editable) {
      url = `${API_URL_CLIENTSIDE}/localizations.json`
    }
    axios
      .get(url)
      .then(r => r.data)
      .then(localizations => {
        commit('setLocalizations', localizations)
      })
  },
  loadMetatexts({ commit }){
    axios
      .get(`${API_URL}/metatexts/all`)
      .then(r => r.data)
      .then(metatexts => {
        commit('setMetatexts', metatexts)
      })
  },
  searchScriptas({ commit }, options){
    let query = options.query
    let exclude = (options.exclude || []).join(',')
    axios
      .get(`${API_URL}/scriptas/autocomplete?query=${query}&exclude=${exclude}`)
      .then(r => r.data)
      .then(scriptas => {
        commit('setScriptas', scriptas)
      })
  },
  async loadLanguages({ commit, dispatch }){
    try {
      const response = config.staticData
        ? await getStatic('languages.json')
        : await axios.get(`${API_URL}/languages/all`)
      const languages = response.data
      if (
        !Array.isArray(languages) ||
        languages.some(language => (
          !language ||
          typeof language.code !== 'string' ||
          typeof language.name !== 'string'
        ))
      ) {
        throw new Error('Language lookup data is malformed')
      }
      commit('setLanguages', languages)
      return languages
    } catch (error) {
      commit('setLanguages', [])
      dispatch('addNotification', {
        type: 'danger',
        text: error.message || 'Languages could not be loaded',
      })
      throw error
    }
  },
  loadScriptas({ commit }){
    let url = `${API_URL}/scriptas/all`
    if (!config.editable) {
      url = `${API_URL_CLIENTSIDE}/scriptas.json`
    }
    axios
      .get(url)
      .then(r => r.data)
      .then(scriptas => {
        commit('setScriptas', scriptas)
      })
  },
  loadTokentypes({ commit }){
    axios
      .get(`${API_URL}/tokentypes/all`)
      .then(r => r.data)
      .then(tokentypes => {
        commit('setTokentype', tokentypes)
      })
  },
  loadOntologies({ commit }){
    axios
      .get(`${API_URL}/ontologies/all`)
      .then(r => r.data)
      .then(ontologies => {
        commit('setOntologies', ontologies)
      })
  },
  loadChangeLogs(_, data){
    return new Promise((resolve) => {
      axios
        .get(`${API_URL}/changelogs/${data.objectType}/${data.objectId}`, data)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
